/**
 * Zone-Based Character Operations Hook
 * 
 * This hook provides character-related operations using the zone-based architecture.
 * It leverages the character zone for isolated character management while maintaining
 * backward compatibility.
 * 
 * Migration from: useCharacterOperations.js
 * Part of: Full Zone Migration
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useCharacterZone, useSharedData } from '../core/useZoneHooks';
import {
  fetchCharactersEnhanced,
  deleteCharacter as deleteCharacterThunk
} from '../../slices/bookWorksheetDataSlice';

export const useCharacterOperationsZoneBased = () => {
  const dispatch = useDispatch();

  // Use zone-based hooks
  const characterZone = useCharacterZone();
  const sharedData = useSharedData();

  // Extract from zones
  const {
    characters,
    selectedCharacter,
    loading: charactersLoading,
    error: charactersError,
    lastUpdated: charactersLastUpdated,
    actions: characterActions
  } = characterZone;

  const {
    selections: { bookId, characterId },
    books
  } = sharedData;

  // Get current book for context
  const currentBook = useMemo(() =>
    books.find(book => book.uuid === bookId) || null,
    [books, bookId]
  );

  // Get characters for current book
  const bookCharacters = useMemo(() =>
    bookId ? characters.filter(char => char.bookId === bookId) : characters,
    [characters, bookId]
  );

  // Cache duration (3 minutes)
  const CACHE_DURATION = 3 * 60 * 1000;

  // Track if we've initiated a fetch for the current bookId
  const fetchInitiatedRef = useRef({});

  // CHARACTER ZONE OPERATIONS
  const fetchCharacters = useCallback(async (targetBookId) => {
    const bookToLoad = targetBookId || bookId;
    if (!bookToLoad) {
      console.error('[useCharacterOperationsZoneBased] No bookId provided');
      return Promise.reject(new Error('No book ID provided'));
    }

    // Check cache
    const now = Date.now();
    if (charactersLastUpdated && (now - charactersLastUpdated < CACHE_DURATION)) {
      const cachedCharacters = characters.filter(char => char.bookId === bookToLoad);
      if (cachedCharacters.length > 0) {
        console.log('[useCharacterOperationsZoneBased] Using cached characters');
        return Promise.resolve({ type: 'cache/hit', data: cachedCharacters });
      }
    }

    console.log('[useCharacterOperationsZoneBased] Loading characters for book:', bookToLoad);
    // Use character zone action
    return characterActions.fetchCharacters(bookToLoad);
  }, [characterActions, bookId, characters, charactersLastUpdated, CACHE_DURATION]);

  const fetchCharactersEnhancedOperation = useCallback(async (targetBookId) => {
    const bookToLoad = targetBookId || bookId;
    if (!bookToLoad) {
      console.error('[useCharacterOperationsZoneBased] No bookId for enhanced load');
      return Promise.reject(new Error('No book ID provided'));
    }

    console.log('[useCharacterOperationsZoneBased] Loading enhanced characters for book:', bookToLoad);
    // Use enhanced fetch for opportunity data
    return dispatch(fetchCharactersEnhanced(bookToLoad)).unwrap();
  }, [dispatch, bookId]);

  const fetchCharacter = useCallback(async (characterId, targetBookId) => {
    const bookToUse = targetBookId || bookId;
    if (!characterId || !bookToUse) {
      console.error('[useCharacterOperationsZoneBased] Missing characterId or bookId');
      return Promise.reject(new Error('Character ID and Book ID required'));
    }

    // Check if character is already loaded
    const existingCharacter = characters.find(c => c.uuid === characterId);
    if (existingCharacter) {
      console.log('[useCharacterOperationsZoneBased] Character already loaded:', characterId);
      return Promise.resolve({ type: 'cache/hit', data: existingCharacter });
    }

    // For now, fetch all characters for the book (since we don't have single character fetch in zone)
    return fetchCharacters(bookToUse);
  }, [characters, bookId, fetchCharacters]);

  const selectCharacter = useCallback((charId) => {
    console.log('[useCharacterOperationsZoneBased] Selecting character:', charId);
    // Use character zone action for selection
    characterActions.selectCharacter(charId);
  }, [characterActions]);

  const setCharacterSelection = selectCharacter; // Alias for backward compatibility

  const createCharacter = useCallback(async (data) => {
    if (!bookId) {
      throw new Error('No book selected for character creation');
    }

    console.log('[useCharacterOperationsZoneBased] Creating character for book:', bookId);
    // Use character zone action
    return characterActions.createCharacter(bookId, data);
  }, [characterActions, bookId]);

  const updateCharacter = useCallback(async (charId, data) => {
    if (!bookId) {
      throw new Error('No book context for character update');
    }

    console.log('[useCharacterOperationsZoneBased] Updating character:', charId);
    // Use character zone action
    return characterActions.updateCharacter(charId, bookId, data);
  }, [characterActions, bookId]);

  const deleteCharacter = useCallback(async (charId, options = {}) => {
    if (!bookId) {
      throw new Error('No book context for character deletion');
    }

    console.log('[useCharacterOperationsZoneBased] Deleting character:', charId);
    // Use the thunk directly for deletion with options
    return dispatch(deleteCharacterThunk({
      characterId: charId,
      bookId,
      options
    })).unwrap();
  }, [dispatch, bookId]);

  const getCharacterDeleteImpact = useCallback((charId) => {
    // Calculate impact of deleting a character
    const character = characters.find(c => c.uuid === charId);
    if (!character) {
      return { variations: 0 };
    }

    // This would need actual implementation to count related variations
    return {
      characterName: character.name || 'Unknown Character',
      variations: 0  // Would need to count from variationZone
    };
  }, [characters]);

  // Form state management (compatibility layer)
  const setCharacterFormDirty = useCallback((isDirty) => {
    // This would map to variation zone's form state
    // For now, log for compatibility
    console.log('[useCharacterOperationsZoneBased] Form dirty state:', isDirty);
  }, []);

  const setCharacterFormSubmitting = useCallback((isSubmitting) => {
    // This would map to variation zone's form state
    console.log('[useCharacterOperationsZoneBased] Form submitting state:', isSubmitting);
  }, []);

  const setCharacterFormErrors = useCallback((errors) => {
    // This would map to variation zone's form state
    console.log('[useCharacterOperationsZoneBased] Form errors:', errors);
  }, []);

  // Auto-load characters when book changes (zone-aware)
  useEffect(() => {
    if (bookId && bookCharacters.length === 0 && !charactersLoading) {
      // Check if we've already initiated a fetch for this bookId
      if (!fetchInitiatedRef.current[bookId]) {
        console.log('[useCharacterOperationsZoneBased] Auto-loading characters for book:', bookId);
        fetchInitiatedRef.current[bookId] = true;

        // Use the character actions directly to avoid dependency issues
        characterActions.fetchCharacters(bookId).catch(error => {
          console.error('[useCharacterOperationsZoneBased] Error fetching characters:', error);
          // Reset the flag on error so we can retry
          delete fetchInitiatedRef.current[bookId];
        });
      }
    }
  }, [bookId, bookCharacters.length, charactersLoading, characterActions]);

  // Clear zone and reset fetch tracking when book changes
  useEffect(() => {
    return () => {
      // Cleanup when unmounting or book changes
      if (bookId) {
        console.log('[useCharacterOperationsZoneBased] Clearing character zone on cleanup');
        // Clear the fetch tracking for this bookId
        delete fetchInitiatedRef.current[bookId];
        characterActions.clearZone();
      }
    };
  }, [bookId, characterActions]);

  return {
    // Character Zone Data
    characters: bookCharacters,
    allCharacters: characters,
    selectedCharacter,
    charactersLoading,
    charactersError,
    charactersLastUpdated,

    // Context Data
    bookId,
    currentBook,
    characterId,

    // Operations
    fetchCharacters,
    fetchCharactersEnhanced: fetchCharactersEnhancedOperation,
    fetchCharacter,
    selectCharacter,
    setCharacterSelection, // Backward compatibility alias
    createCharacter,
    updateCharacter,
    deleteCharacter,
    getCharacterDeleteImpact,

    // Form state management (compatibility)
    setCharacterFormDirty,
    setCharacterFormSubmitting,
    setCharacterFormErrors,
    characterFormState: {
      isDirty: false,
      isSubmitting: false,
      errors: null
    },

    // Zone Management
    clearCharacterZone: characterActions.clearZone,

    // Direct zone access for advanced use
    characterZone,
    sharedData
  };
};

// Export as default
export default useCharacterOperationsZoneBased;

// Also export with old name for easier migration
export const useCharacterOperations = useCharacterOperationsZoneBased;
